/**
 * SbaDocumentResults — Document-level search results for SBA Review Mode.
 *
 * Shows ranked SBA documents as cards with:
 * - Trade name, applicant, application number
 * - Relevance score with concept coverage badges
 * - Top evidence sections (expandable)
 * - Rows are REAL links → left click navigates in-tab, middle/cmd click
 *   opens a background tab, right click offers the browser menu
 */
import React, { useCallback, useState } from 'react';
import {
  FileText,
  ChevronDown,
  ChevronRight,
  Search,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  SBADocumentEvidence,
  SBADocumentResult,
  SBADocumentSearchConcept,
  SBASubmissionAssociation,
} from '../types/sba';

interface SbaDocumentResultsProps {
  documents: SBADocumentResult[];
  isLoading: boolean;
  isNoQuery: boolean;
  statusMessage: string;
  concepts: SBADocumentSearchConcept[];
  documentCount: number;
  /** Canonically ordered labels from the applied (submitted) filter set. */
  appliedDocTypeLabels?: string[];
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

const logicalKeyFor = (document: SBADocumentResult): string =>
  document.logical_document_key || `physical:${document.document_id}`;

const typeLabelsFor = (document: SBADocumentResult): string[] => {
  if (document.effective_doc_type_labels?.length) {
    return document.effective_doc_type_labels;
  }
  return document.doc_type_label ? [document.doc_type_label] : ['FDA review'];
};

/**
 * `null` means the additive association field was absent (rolling deploy);
 * an empty array is authoritative and means the source is unclassified.
 */
const submissionAssociationsFor = (
  document: SBADocumentResult,
): SBASubmissionAssociation[] | null => {
  if (Array.isArray(document.submission_associations)) {
    return document.submission_associations;
  }
  if (document.fda_submission_id != null) {
    return [{
      fda_submission_id: document.fda_submission_id,
      submission_type: document.submission_type ?? null,
      submission_number: document.submission_number ?? null,
      submission_source: document.submission_source ?? null,
      submission_status_date: document.submission_status_date ?? null,
      submission_class_code: document.submission_class_code ?? null,
    }];
  }
  return null;
};

const submissionLabel = (association: SBASubmissionAssociation): string => {
  const type = association.submission_type?.toUpperCase();
  if (type === 'ORIG') return 'Original submission';
  if (type === 'SUPPL') {
    return association.submission_number == null
      ? 'Supplement'
      : `Supplement ${association.submission_number}`;
  }
  if (association.submission_type) {
    return association.submission_number == null
      ? association.submission_type
      : `${association.submission_type} ${association.submission_number}`;
  }
  return 'FDA review';
};

const formatStatusDate = (value: string | null): string | null => {
  if (!value) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00Z`)
    : new Date(value);
  if (Number.isNaN(dateOnly.getTime())) return null;
  return dateOnly.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const resultIdentity = (document: SBADocumentResult): string => {
  const associations = submissionAssociationsFor(document);
  const associationKey = associations === null
    ? 'legacy'
    : associations.map(item => item.fda_submission_id).join(',');
  return [
    document.trade_name,
    document.application_type || '',
    document.application_number,
    typeLabelsFor(document).join('|'),
    associationKey,
  ].join('::');
};

const resultCopy = (
  count: number,
  labels: string[],
  loading: boolean,
): { text: string; title?: string; ariaLabel?: string } => {
  const noun = count === 1 ? 'document' : 'documents';
  const suffix = loading ? ' so far...' : '';
  if (labels.length === 1) {
    return { text: `${count} ${labels[0]} ${noun} found${suffix}` };
  }
  if (labels.length >= 2) {
    const boundedLabels = labels.length <= 3
      ? labels.join(' + ')
      : `${labels[0]} + ${labels[1]} + ${labels.length - 2} more`;
    const copy = {
      text: `${count} HA ${noun} found · ${boundedLabels}${suffix}`,
      title: labels.join(', '),
    };
    return labels.length > 3
      ? {
          ...copy,
          ariaLabel: `${count} HA ${noun} found for ${labels.join(', ')}${suffix}`,
        }
      : copy;
  }
  return { text: `${count} HA ${noun} found${suffix}` };
};

/**
 * Route for a document card: the parent drug's viewer, deep-linked to the
 * chunks owned by THIS physical document. ``null`` when the document has no
 * drug to open, so the caller renders plain text instead of a dead link.
 */
export function sbaDocumentHref(doc: SBADocumentResult): string | null {
  if (doc.drug_id == null) return null;
  const params = new URLSearchParams();
  params.set('source', 'sba');
  params.set('version', String(doc.document_id));
  const chunkIds = doc.evidence_sections
    .filter((e) => (e.document_id ?? doc.document_id) === doc.document_id)
    .map((e) => e.chunk_id)
    .filter((id): id is number => typeof id === 'number');
  if (chunkIds.length > 0) {
    params.set('sbaChunkIds', chunkIds.join(','));
  }
  return `/drug/${doc.drug_id}?${params.toString()}`;
}

/** Route for one evidence row: its own physical document and exact chunk. */
export function sbaEvidenceHref(
  doc: SBADocumentResult,
  evidence: SBADocumentEvidence,
): string | null {
  if (doc.drug_id == null) return null;
  const params = new URLSearchParams();
  params.set('source', 'sba');
  params.set('version', String(evidence.document_id ?? doc.document_id));
  params.set('sbaChunkIds', String(evidence.chunk_id));
  return `/drug/${doc.drug_id}?${params.toString()}`;
}

export default function SbaDocumentResults({
  documents,
  isLoading,
  isNoQuery,
  statusMessage,
  concepts,
  documentCount,
  appliedDocTypeLabels = [],
  currentPage = 1,
  pageSize = 50,
  onPageChange,
}: SbaDocumentResultsProps) {
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((documentKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (next.has(documentKey)) {
        next.delete(documentKey);
      } else {
        next.add(documentKey);
      }
      return next;
    });
  }, []);

  const identityCounts = new Map<string, number>();
  documents.forEach((document) => {
    const identity = resultIdentity(document);
    identityCounts.set(identity, (identityCounts.get(identity) ?? 0) + 1);
  });
  const conceptLabelsById = new Map(
    concepts.map(concept => [concept.id, concept.concept]),
  );

  const heading = resultCopy(documentCount, appliedDocTypeLabels, isLoading);
  const totalPages = Math.max(1, Math.ceil(documentCount / pageSize));

  // Empty state: no query entered
  if (isNoQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center mb-6">
          <Search className="w-8 h-8 text-indigo-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          Search HA Documents
        </h3>
        <p className="text-slate-500 max-w-md">
          Enter a query to search across FDA health-authority review documents.
          Results are ranked by relevance with evidence sections highlighted.
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-4">
          <div className="w-10 h-10 border-3 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-10 h-10 border-3 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
        </div>
        <p className="text-sm text-slate-600 font-medium">{statusMessage || 'Searching...'}</p>
        {concepts.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 justify-center">
            {concepts.map(c => (
              <span
                key={c.id}
                className={`text-xs px-2.5 py-1 rounded-full ${
                  c.anchor
                    ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {c.concept}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // No results
  if (documents.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center mb-4">
          <FileText className="w-7 h-7 text-slate-300" />
        </div>
        <h3
          className="text-base font-medium text-slate-600 mb-1"
          title={appliedDocTypeLabels.length > 0 ? appliedDocTypeLabels.join(', ') : undefined}
          aria-label={appliedDocTypeLabels.length > 3
            ? `No HA documents found for ${appliedDocTypeLabels.join(', ')}`
            : undefined}
        >
          {appliedDocTypeLabels.length === 1
            ? `No ${appliedDocTypeLabels[0]} documents found`
            : appliedDocTypeLabels.length > 1
              ? `No HA documents found for ${appliedDocTypeLabels.length <= 3
                ? appliedDocTypeLabels.join(' + ')
                : `${appliedDocTypeLabels[0]} + ${appliedDocTypeLabels[1]} + ${appliedDocTypeLabels.length - 2} more`}`
              : 'No HA documents found'}
        </h3>
        <p className="text-sm text-slate-400">Try adjusting your search terms.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Results header */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-slate-500">
          <span title={heading.title} aria-label={heading.ariaLabel}>
            {heading.text}
          </span>
        </p>
        {concepts.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
            <div className="flex gap-1">
              {concepts.map(c => (
                <span
                  key={c.id}
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    c.anchor
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'bg-slate-50 text-slate-500'
                  }`}
                >
                  {c.concept.length > 25 ? c.concept.slice(0, 25) + '...' : c.concept}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Streaming progress indicator */}
      {isLoading && documents.length > 0 && (
        <div className="flex items-center gap-3 py-2.5 px-4 bg-indigo-50/60 border border-indigo-100 rounded-lg">
          <div className="relative flex-shrink-0">
            <div className="w-4 h-4 border-2 border-indigo-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-4 h-4 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <span className="text-sm text-indigo-700">{statusMessage}</span>
        </div>
      )}

      {/* Document cards */}
      {documents.map((doc, index) => {
        const documentKey = logicalKeyFor(doc);
        const isExpanded = expandedDocs.has(documentKey);
        const topEvidence = doc.evidence_sections.slice(0, isExpanded ? undefined : 2);
        const hasMoreEvidence = doc.evidence_sections.length > 2;
        const typeLabels = typeLabelsFor(doc);
        const associations = submissionAssociationsFor(doc);
        const showSourceName =
          identityCounts.get(resultIdentity(doc))! > 1 && !!doc.source_document_name;
        const statusDates = Array.from(new Set(
          (associations ?? [])
            .map(item => formatStatusDate(item.submission_status_date))
            .filter((date): date is string => !!date),
        ));
        const docHref = sbaDocumentHref(doc);
        return (
          <div
            key={documentKey}
            data-testid="sba-document-card"
            data-logical-document-key={documentKey}
            className="relative bg-white border border-slate-200 rounded-xl hover:border-indigo-200 hover:shadow-md transition-all group"
          >
            {/* Document header */}
            <div className="px-5 py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {/* Rank badge */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm">
                    {(currentPage - 1) * pageSize + index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-slate-800 truncate group-hover:text-indigo-700 transition-colors">
                      {docHref ? (
                        // Stretched link: ``after:absolute after:inset-0`` grows this
                        // anchor's hit area to cover the whole card, so the card stays
                        // clickable end-to-end while every click is a REAL link click —
                        // middle-click and cmd-click open a background tab, right-click
                        // offers "Open in new tab", and the URL shows on hover.
                        <Link
                          to={docHref}
                          className="rounded after:absolute after:inset-0 after:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        >
                          {doc.trade_name}
                        </Link>
                      ) : (
                        doc.trade_name
                      )}
                    </h3>
                    <div className="mt-0.5 text-sm font-medium text-indigo-600">
                      {typeLabels.join(' + ')}
                    </div>
                    {showSourceName && (
                      <div className="mt-0.5 text-xs text-slate-400 truncate">
                        {doc.source_document_name}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                      {doc.applicant && <span>{doc.applicant}</span>}
                      {doc.application_number && (
                        <span className="text-slate-400">
                          {doc.application_type ? `${doc.application_type} ` : ''}
                          {doc.application_number}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {associations === null ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          FDA review
                        </span>
                      ) : associations.length === 0 ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-100">
                          Unclassified review
                        </span>
                      ) : associations.map(association => (
                        <span
                          key={association.fda_submission_id}
                          className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-100"
                        >
                          {submissionLabel(association)}
                        </span>
                      ))}
                      {statusDates.map(date => (
                        <span key={date} className="text-xs text-slate-500">
                          {date}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Score */}
                <div className="flex-shrink-0 ml-4 text-right">
                  <div className="text-sm font-semibold text-indigo-600">
                    {Math.round(doc.score * 100)}%
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">relevance</div>
                </div>
              </div>

              {/* Concept coverage badges */}
              {doc.bucket_coverage.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {doc.bucket_coverage.map(bucket => (
                    <span
                      key={bucket}
                      className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                    >
                      {conceptLabelsById.get(bucket) ?? bucket}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Evidence sections */}
            <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/50 rounded-b-xl">
              <div className="space-y-2">
                {topEvidence.map((ev, evIdx) => {
                  const evHref = sbaEvidenceHref(doc, ev);
                  const evKey = `${ev.document_id ?? doc.document_id}:${ev.chunk_id}:${evIdx}`;
                  const evBody = (
                    <>
                      <span className="flex-shrink-0 text-xs font-medium text-slate-400 mt-0.5 w-12">
                        {ev.section_number || '?'}
                      </span>
                      <div className="min-w-0 flex-1">
                        {ev.section_title && (
                          <span className="text-xs font-medium text-indigo-500 mr-1.5">
                            {ev.section_title}
                          </span>
                        )}
                        <span className="text-slate-600 leading-relaxed">
                          {ev.chunk_text.length > 200
                            ? ev.chunk_text.slice(0, 200) + '...'
                            : ev.chunk_text}
                        </span>
                      </div>
                    </>
                  );
                  // ``relative z-10`` lifts the row above the card's stretched
                  // overlay so it keeps its OWN href (its own physical document +
                  // chunk) instead of being swallowed by the card-level link.
                  return evHref ? (
                    <Link
                      key={evKey}
                      to={evHref}
                      aria-label={`Open evidence ${ev.section_number || ev.chunk_id}`}
                      className="relative z-10 flex w-full gap-2 text-left text-sm rounded p-1 -m-1 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {evBody}
                    </Link>
                  ) : (
                    <div
                      key={evKey}
                      className="relative z-10 flex w-full gap-2 text-left text-sm p-1 -m-1"
                    >
                      {evBody}
                    </div>
                  );
                })}
              </div>

              {/* Expand/collapse button */}
              {hasMoreEvidence && (
                <button
                  type="button"
                  className="relative z-10 flex items-center gap-1 mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition-colors"
                  onClick={(e) => toggleExpanded(documentKey, e)}
                >
                  {isExpanded ? (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-3.5 h-3.5" />
                      {doc.evidence_sections.length - 2} more evidence sections
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {onPageChange && totalPages > 1 && (
        <div className="flex items-center justify-between px-1 pt-2">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={isLoading || currentPage <= 1}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={isLoading || currentPage >= totalPages}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
